export var FlowObsoleteBlockType;
(function (FlowObsoleteBlockType) {
    /**
     * Permanently obsolete blocks. These can never execute.
     * These blocks are no longer used, but are kept around to protect from reuse.
     * In other words, there are old flows that could have an obsolete block type name.
     * That block name can not be reused for new blocks with different implementations.
     */
    FlowObsoleteBlockType["ObsoleteHumanReview"] = "HumanReview";
    FlowObsoleteBlockType["ObsoleteApiSubmissions"] = "ApiSubmissions";
    FlowObsoleteBlockType["ObsoleteGetEncounterSummary"] = "GetEncounterSummary";
    FlowObsoleteBlockType["ObsoleteSendToLabelboxForReview"] = "SendToLabelboxForReview";
    FlowObsoleteBlockType["ObsoleteWaitForAvailableSchedulingSlot"] = "WaitForAvailableSchedulingSlot";
    FlowObsoleteBlockType["ObsoleteSkill"] = "Skill";
})(FlowObsoleteBlockType || (FlowObsoleteBlockType = {}));
export var FlowBlockType;
(function (FlowBlockType) {
    FlowBlockType["Output"] = "Output";
    FlowBlockType["CareGapAlgorithmBlock"] = "CareGapAlgorithmBlock";
    FlowBlockType["HCCAlgorithmBlock"] = "HCCAlgorithmBlock";
    FlowBlockType["LlmPrompt"] = "LlmPrompt";
    FlowBlockType["Start"] = "Start";
    FlowBlockType["SubFlowV1"] = "SubFlow";
    FlowBlockType["SubFlowV2"] = "SubFlowV2";
    FlowBlockType["SkillV2"] = "SkillV2";
    FlowBlockType["ScriptedFlow"] = "ScriptedFlow";
    FlowBlockType["Switch"] = "Switch";
    FlowBlockType["Wait"] = "Wait";
    FlowBlockType["WaitV2"] = "WaitV2";
    FlowBlockType["Rest"] = "Rest";
    FlowBlockType["DocumentExtract"] = "DocumentExtract";
    FlowBlockType["DocumentTemplate"] = "DocumentTemplate";
    FlowBlockType["GenerateFile"] = "GenerateFile";
    FlowBlockType["AdvancedFhir"] = "AdvancedFhir";
    FlowBlockType["Fhir"] = "Fhir";
    FlowBlockType["WaitForSignal"] = "WaitForSignal";
    FlowBlockType["WaitForFormCompletion"] = "WaitForFormCompletion";
    FlowBlockType["Form"] = "Form";
    FlowBlockType["FormV2"] = "FormV2";
    FlowBlockType["GetNotableObject"] = "GetNotableObject";
    FlowBlockType["GetPatient"] = "GetPatient";
    FlowBlockType["Ocr"] = "Ocr";
    FlowBlockType["OcrV2"] = "OcrV2";
    FlowBlockType["GetPatientOrder"] = "GetPatientOrder";
    FlowBlockType["AsynchronousExtractJob"] = "AsynchronousExtractJob";
    FlowBlockType["AsynchronousExportJob"] = "AsynchronousExportJob";
    FlowBlockType["GetProvider"] = "GetProvider";
    FlowBlockType["ExportExternalDocumentPacket"] = "ExportExternalDocumentPacket";
    FlowBlockType["ExportTextToBucket"] = "ExportTextToBucket";
    FlowBlockType["GetExternalDocumentPacket"] = "GetExternalDocumentPacket";
    FlowBlockType["GetFormSet"] = "GetFormSet";
    FlowBlockType["Scheduling"] = "Scheduling";
    FlowBlockType["VoiceCall"] = "VoiceCall";
    FlowBlockType["VoiceCallV2"] = "VoiceCallV2";
    FlowBlockType["InsuranceCard"] = "InsuranceCard";
    FlowBlockType["Callback"] = "Callback";
    FlowBlockType["Evaluation"] = "Evaluation";
    FlowBlockType["VoiceTranscribe"] = "VoiceTranscribe";
    FlowBlockType["HumanTask"] = "HumanTask";
    FlowBlockType["SendEmail"] = "SendEmail";
    FlowBlockType["SendSms"] = "SendSms";
    FlowBlockType["Conversation"] = "Conversation";
    FlowBlockType["CreateEncounterSummary"] = "CreateEncounterSummary";
    FlowBlockType["CreatePatientOrder"] = "CreatePatientOrder";
    FlowBlockType["PatientDataStore"] = "PatientDataStore";
    FlowBlockType["MedicalNecessityCheck"] = "MedicalNecessityCheck";
    FlowBlockType["FilterList"] = "FilterList";
    FlowBlockType["AuthorizationSubmission"] = "AuthorizationSubmission";
    FlowBlockType["TestLifecycle"] = "TestLifeCycle";
    FlowBlockType["InformationExtraction"] = "InformationExtraction";
    FlowBlockType["Fax"] = "Fax";
    FlowBlockType["UpdateFlowConfig"] = "UpdateFlowConfig";
    FlowBlockType["Hl7"] = "HL7";
    FlowBlockType["IteratorV1"] = "Iterator";
    FlowBlockType["IteratorV2"] = "IteratorV2";
    FlowBlockType["Repeat"] = "Repeat";
    FlowBlockType["HieQueryDocuments"] = "HieQueryDocuments";
    FlowBlockType["HieGetDocuments"] = "HieGetDocuments";
    FlowBlockType["GetInsurances"] = "GetInsurances";
    FlowBlockType["DocumentSearch"] = "DocumentSearch";
    FlowBlockType["RestApiClassicAuthorization"] = "RestApiClassicAuthorization";
    FlowBlockType["GetSftpFile"] = "GetSftpFile";
    FlowBlockType["UpdateNotableObject"] = "UpdateNotableObject";
    FlowBlockType["CreateNotableObject"] = "CreateNotableObject";
    FlowBlockType["RealTimeEligibility"] = "RealTimeEligibility";
    FlowBlockType["FileConversion"] = "FileConversion";
    FlowBlockType["Mirth"] = "Mirth";
    FlowBlockType["Text"] = "Text";
    FlowBlockType["ServiceReview"] = "ServiceReview";
    FlowBlockType["Conditional"] = "Conditional";
    FlowBlockType["MontageOnBase"] = "MontageOnBase";
})(FlowBlockType || (FlowBlockType = {}));
/**
 * Prioritize a block, lower number == higher priority.
 * A higher priority block will be executed before other Queued blocks with lower priorities.
 * This mostly exists to defer the slower or waiting blocks after faster ones.
 **/
const FlowBlockPriority = {
    [FlowBlockType.HumanTask]: 2000,
    [FlowBlockType.Wait]: 1000,
    [FlowBlockType.WaitV2]: 1000,
    [FlowBlockType.WaitForSignal]: 1000,
    [FlowBlockType.Callback]: 1000,
    [FlowBlockType.DocumentTemplate]: 1000,
    [FlowBlockType.Form]: 1000,
    [FlowBlockType.FormV2]: 1000,
    [FlowBlockType.Fax]: 1000,
    [FlowBlockType.VoiceCall]: 1000,
    [FlowBlockType.VoiceCallV2]: 1000,
    [FlowBlockType.SubFlowV1]: 900,
    [FlowBlockType.SubFlowV2]: 900,
    [FlowBlockType.SkillV2]: 900,
    [FlowBlockType.CareGapAlgorithmBlock]: 800,
    [FlowBlockType.HCCAlgorithmBlock]: 800,
    [FlowBlockType.Mirth]: 600,
    [FlowBlockType.Ocr]: 500,
    [FlowBlockType.OcrV2]: 500,
    [FlowBlockType.DocumentExtract]: 500,
    [FlowBlockType.PatientDataStore]: 400,
    [FlowBlockType.AdvancedFhir]: 400,
    [FlowBlockType.Fhir]: 400,
    [FlowBlockType.Rest]: 400,
    [FlowBlockType.LlmPrompt]: 300,
    [FlowBlockType.ScriptedFlow]: 100,
    [FlowBlockType.Switch]: 100,
    [FlowBlockType.TestLifecycle]: 50,
    [FlowBlockType.Start]: 1,
};
const DEFAULT_FLOW_BLOCK_PRIORITY = 200;
export function getFlowBlockTypePriority(blockType) {
    return FlowBlockPriority[blockType] ?? DEFAULT_FLOW_BLOCK_PRIORITY;
}
//# sourceMappingURL=type.js.map